import { NavItemType } from 'types/menu';

/**
 * Maps menu item URLs to their react-intl message IDs.
 * The API returns UUIDs as item.id, so we use the URL path to resolve
 * the correct translation key instead.
 */
const URL_TO_INTL_ID: Record<string, string> = {
  '/home': 'menu.home',
  '/dashboard': 'menu.dashboard',
  '/reports': 'menu.report',
  '/personnels': 'menu.account_personnels',
  '/personnels/information': 'menu.information',
  '/personnels/patrols': 'menu.patrols',
  '/customers': 'menu.customers',
  '/leave': 'menu.leave',
  '/disbursements/payments': 'menu.disbursements-payments',
  '/disbursements/devices': 'menu.disbursements-devices',
  '/financials': 'menu.financials',
  '/settings': 'menu.settings',
  '/settings/organizations/information': 'menu.organizations',
  '/settings/organizations/structures': 'menu.organizations-structures',
  '/settings/positions': 'menu.organizations-positions',
  '/settings/organizations/benefit': 'menu.welfare',
  '/settings/accounts': 'menu.add_personnels',
  '/settings/organizations/shift-work': 'menu.organizations-leave',
  '/organizations/master': 'menu.organizations-master',
  '/signup-wizard': 'menu.signup-wizard',
  '/inspection-plans': 'menu.inspection-plans',
  '/incident': 'menu.incident',
  '/incident-reports': 'menu.incident',
  '/meet-customers': 'menu.meet-customers',
  '/equipment': 'menu.equipment',
  '/settings/welfare/leave': 'menu.welfare-leave',
  '/settings/welfare/finance/cash-advance': 'menu.welfare-finance',
  '/settings/organizations/shiftwork': 'menu.organizations-leave',
  '/settings/equipment': 'menu.equipment',
  '/settings/device-activation': 'menu.device-activation',
};

/**
 * Fallback mapping for collapse items that don't have a distinguishable URL.
 * Uses the Thai title returned by the API to identify the translation key.
 */
const COLLAPSE_TITLE_TO_INTL_ID: Record<string, string> = {
  'ข้อมูลบุคลากร': 'menu.account_collapse',
  'บุคลากร': 'menu.account_collapse',
  'การเบิกจ่าย': 'menu.disbursements_collapse',
  'ข้อมูลการเบิกจ่าย': 'menu.disbursements_collapse',
  'การเบิกจ่าย ': 'menu.disbursements_collapse',
  'ตั้งค่า': 'menu.settings',
};

/**
 * Resolve the react-intl message ID for a menu item.
 * Strategy: URL mapping → collapse title mapping → item.id fallback.
 */
export function getMenuIntlId(item: NavItemType): string | undefined {
  // 1. Try URL-based mapping (most reliable)
  if (item.url && item.url !== '#') {
    const key = URL_TO_INTL_ID[item.url];
    if (key) return key;
  }

  // 2. For collapse items, try title-based mapping
  if (item.type === 'collapse' && item.title) {
    const titleStr = String(item.title).trim();
    const key = COLLAPSE_TITLE_TO_INTL_ID[titleStr];
    if (key) return key;
  }

  // 3. If item.id is a human-readable key (not a UUID or numeric), try it directly
  if (item.id && !/^[0-9a-f]{8}-/.test(item.id) && !/^\d+$/.test(item.id)) {
    return `menu.${item.id}`;
  }

  return undefined;
}
